export type WorkshopCommunityViewerRole = 'confirmed' | 'interest';

export function formatCommunityCardTitle(
  viewerRole: WorkshopCommunityViewerRole,
  confirmedCount: number
): string {
  if (viewerRole === 'interest') {
    return 'Workshop community';
  }
  return `Workshop Participants (${confirmedCount})`;
}

export function formatCommunityCardSubtitle(
  viewerRole: WorkshopCommunityViewerRole,
  confirmedCount: number,
  interestCount: number,
  workshopStarted: boolean
): string | undefined {
  if (viewerRole === 'confirmed') {
    if (workshopStarted || interestCount === 0) {
      return undefined;
    }
    return interestCount === 1
      ? 'Plus 1 on the interest list'
      : `Plus ${interestCount} on the interest list`;
  }

  if (workshopStarted) {
    return confirmedCount === 1 ? '1 confirmed' : `${confirmedCount} confirmed`;
  }

  const confirmedPart = confirmedCount === 1 ? '1 confirmed' : `${confirmedCount} confirmed`;
  const interestPart =
    interestCount === 1 ? '1 on the interest list' : `${interestCount} on the interest list`;
  return `${confirmedPart} · ${interestPart}`;
}

export function formatConfirmedRosterNames(
  visibleNames: string[],
  namesHiddenCount: number,
  confirmedCount: number
): string {
  if (confirmedCount === 0) {
    return 'No confirmed participants yet.';
  }

  if (visibleNames.length === 0) {
    const people = namesHiddenCount === 1 ? '1 person has' : `${namesHiddenCount} people have`;
    return `${people} confirmed and will be joining.`;
  }

  let text = visibleNames.join(', ');
  if (namesHiddenCount > 0) {
    const other = namesHiddenCount === 1 ? '1 other' : `${namesHiddenCount} others`;
    text += `, + ${other}`;
  }
  return `${text}.`;
}

export function formatInterestListSummary(interestCount: number): string {
  if (interestCount === 0) {
    return 'No one is on the interest list right now.';
  }
  if (interestCount === 1) {
    return 'One person is on the interest list and hasn\u2019t confirmed yet.';
  }
  return `${interestCount} people are on the interest list and haven\u2019t confirmed yet.`;
}

export function formatInterestedViewerLines(
  confirmedCount: number,
  interestCount: number,
  workshopStarted: boolean,
  includesViewer: boolean
): string[] {
  if (workshopStarted) {
    if (confirmedCount === 1) {
      return ['1 person has confirmed.'];
    }
    return [`${confirmedCount} people have confirmed.`];
  }

  const lines: string[] = [];
  if (confirmedCount === 1) {
    lines.push('1 person has confirmed they\u2019re planning to attend.');
  } else {
    lines.push(`${confirmedCount} people have confirmed they\u2019re planning to attend.`);
  }

  if (interestCount > 0) {
    const suffix = includesViewer ? ' (including you)' : '';
    if (interestCount === 1) {
      lines.push(`1 person is on the interest list and hasn\u2019t confirmed yet${suffix}.`);
    } else {
      lines.push(
        `${interestCount} people are on the interest list and haven\u2019t confirmed yet${suffix}.`
      );
    }
  }

  return lines;
}
